import React, { useEffect, useState } from "react";
import ProductList from "./components/ProductList";
import Landing from "./components/Auth/Landing";
import Login from "./components/Auth/Login";
import Capture from "./components/Capture";
import PWAInstallBanner from "./components/PWAInstallBanner";
import SyncStatusIndicator from "./components/SyncStatusIndicator";
import UserMenu from "./components/UserMenu";
import { useAuth } from "./hooks/useAuth";
import { syncService } from "./services/syncService";
import { productIdFor } from "./utils/product";

const App: React.FC = () => {
  const { isAuthenticated, loading, user } = useAuth();
  // Le flux est accueil → capture, et rien d'autre : plus de barre d'onglets.
  // L'écran de capture, c'est un produit ouvert — « Démarrer » en ouvre un
  // neuf, « Ajouter des photos » rouvre celui qu'on a déjà photographié. Aucun
  // produit n'existe tant qu'aucune photo ne le nomme : ouvrir le viseur puis
  // en ressortir sans déclencher ne laisse donc rien derrière soi.
  const [openProduct, setOpenProduct] = useState<string | null>(null);
  const [showLanding, setShowLanding] = useState(true);

  // Le compte connu est ce qui manque au service d'envoi pour partir seul : il
  // le lui donne ici, une fois, et le reste — démarrage, retour du réseau,
  // retour au premier plan — se déclenche chez lui. Le démarrage reprend au
  // passage les photos qu'une session fermée en plein envoi a laissées en
  // cours.
  useEffect(() => {
    syncService.setAccount(user);
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-dvh bg-base-200">
        <span className="loading loading-spinner loading-lg text-primary"></span>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (showLanding) {
      return <Landing onContinue={() => setShowLanding(false)} />;
    }
    return (
      <div className="min-h-dvh bg-base-200 flex flex-col items-center justify-center p-4">
        <Login />
      </div>
    );
  }

  return (
    <div className="h-dvh flex flex-col bg-base-100 overflow-hidden">
      {/* L'indicateur d'envoi vit ici, et non plus dans l'écran de capture :
          l'envoi court désormais après un « Terminer », donc il doit se lire
          à l'accueil aussi. */}
      <SyncStatusIndicator />

      {/* Header */}
      <header className="bg-primary text-primary-content p-4 shadow-md flex justify-between items-center z-30">
        {openProduct !== null ? (
          /* La sortie qui abandonne le dit : une flèche de retour, muette, était
             un piège à côté d'un « Terminer » qui, lui, envoie. Nommée, elle
             devient un choix. Pas de `btn-ghost` ici : il repose la couleur de
             texte du thème sur le fond primaire, et tombe à 2,13:1 — un bouton
             nu en `text-primary-content` mesure comme le titre voisin. */
          <button
            type="button"
            className="-ml-1 flex min-h-11 items-center rounded-lg px-3 text-sm font-semibold text-primary-content underline underline-offset-4"
            onClick={() => setOpenProduct(null)}
          >
            ✕ Fermer sans envoyer
          </button>
        ) : (
          <h1 className="text-xl font-bold tracking-tight">TPP Traçabilité</h1>
        )}
        <UserMenu />
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-hidden relative">
        {openProduct !== null ? (
          <Capture
            initialProduct={openProduct}
            onFinish={() => setOpenProduct(null)}
          />
        ) : (
          <ProductList
            onStart={(productId) =>
              setOpenProduct(productId ?? productIdFor(new Date()))
            }
          />
        )}
      </main>

      {/* PWA Install Banner */}
      <PWAInstallBanner />
    </div>
  );
};

export default App;
